import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, TitleStrategy } from '@angular/router';
import { environment } from '@environment/environment';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ResilientTranslateLoader } from '@shared/loaders/resilient-translate.loader';
import { CoreRoutingModule } from '@references/core/core-routing.module';
import { BrandedTitleStrategy } from '@references/shared/services/branded-title.strategy';
import { ReferencesSharedModule } from '@references/shared/shared.module';
import { InterceptorService } from '@shared/services/interceptor.service';
import { CustomPreloadingStrategy } from '@shared/strategies/custom.preloading.strategy';
import { ChartModule, HIGHCHARTS_MODULES } from 'angular-highcharts';
import more from 'highcharts/esm/highcharts-more.src';
import drilldown from 'highcharts/esm/modules/drilldown.src';
import Highcharts from 'highcharts/esm/highcharts.src';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { RatingModule } from 'ngx-bootstrap/rating';
import { SortableModule } from 'ngx-bootstrap/sortable';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NgXCableModule } from '@shared/modules/action-cable.module';
import { ColorPickerDirective } from 'ngx-color-picker';
import { CookieService } from 'ngx-cookie-service';
import { GooglePlaceModule } from '@shared/modules/google-places';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { provideNgxMask } from 'ngx-mask';
import { FacebookPixelModule } from '@shared/modules/facebook-pixel.module';
import { QuillModule } from 'ngx-quill';
import { ToastrModule } from 'ngx-toastr';
import { UiSwitchModule } from 'ngx-ui-switch';
import { provideNgxWebstorage, withLocalStorage, withNgxWebstorageConfig, withSessionStorage } from 'ngx-webstorage';

// The app has no dark theme; keep Highcharts 13's adaptive palette from
// following the OS color scheme (black chart backgrounds on dark-mode systems)
Highcharts.setOptions({ palette: { colorScheme: 'light' } });

// AoT requires an exported function for factories
export const createTranslateLoader = (http: HttpClient) =>
  new ResilientTranslateLoader(
    new TranslateHttpLoader(http, './assets/i18n/', '.json')
  );

@Injectable({
  providedIn: 'root'
})
@NgModule({ exports: [RouterModule, ReferencesSharedModule],
    declarations: [], imports: [ReferencesSharedModule,
        ColorPickerDirective,
        BrowserModule,
        BrowserAnimationsModule,
        ChartModule,
        CoreRoutingModule,
        NgxSliderModule,
        GooglePlaceModule,
        NgXCableModule,
        NgIdleKeepaliveModule.forRoot(),
        FacebookPixelModule.forRoot({
            enabled: environment.FB_PIXEL.IS_ENABLED,
            pixelId: environment.FB_PIXEL.PIXEL_ID
        }),
        RatingModule,
        TabsModule,
        ButtonsModule,
        BsDatepickerModule,
        PopoverModule,
        QuillModule.forRoot(),
        BsDropdownModule,
        ModalModule,
        TooltipModule,
        TypeaheadModule,
        PaginationModule,
        SortableModule,
        UiSwitchModule.forRoot({
            size: 'small',
            color: '#21CE99',
            defaultBgColor: '#FF2721',
            defaultBoColor: 'rgba(0, 0, 0, 0.1)'
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient]
            }
        }),
        LoggerModule.forRoot({
            // serverLoggingUrl: '/logs',
            level: environment.IS_PRODUCTION
                ? NgxLoggerLevel.ERROR
                : NgxLoggerLevel.DEBUG
            // serverLogLevel: NgxLoggerLevel.ERROR
        }),
        ToastrModule.forRoot({
            // 2000ms was far too short to read (WCAG P2-6); errors get an even
            // longer window via ToastService.
            timeOut: 8000,
            toastClass: 'ngx-toastr v-toastr',
            preventDuplicates: true
        })], providers: [
        provideNgxWebstorage(withNgxWebstorageConfig({ prefix: 'references.vitay.io' }), withLocalStorage(), withSessionStorage()),
        CustomPreloadingStrategy,
        CookieService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: InterceptorService,
            multi: true
        },
        {
            provide: HIGHCHARTS_MODULES,
            useFactory: () => [more, drilldown]
        },
        {
            provide: TitleStrategy,
            useClass: BrandedTitleStrategy
        },
        provideNgxMask(),
        provideHttpClient(withXhr(), withInterceptorsFromDi())
    ] })
export class CoreModule {}
